import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Bell } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

export default function PortalShell({ nav, roleLabel, children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 brand-gradient-navy flex-col fixed inset-y-0 z-30">
        <div className="p-5 border-b border-white/10"><Logo variant="light" showTagline /></div>
        <div className="px-4 py-3 text-xs uppercase tracking-wider text-sky-300/70">{roleLabel}</div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`portal-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-[#0088FF] text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`}>
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 text-sm text-slate-300 truncate">{user?.name}</div>
          <button onClick={doLogout} data-testid="portal-logout" className="flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-sm text-rose-300 hover:bg-white/10">
            <LogOut className="h-5 w-5" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 md:ml-64 flex flex-col pb-20 md:pb-0">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 px-5 h-16 flex items-center justify-between">
          <h1 className="font-display font-bold text-lg text-[#0A192F]">{title}</h1>
          <div className="flex items-center gap-3">
            <NavLink to="notifications" className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="portal-bell"><Bell className="h-5 w-5 text-slate-500" /></NavLink>
            <div className="md:hidden"><button onClick={doLogout} data-testid="portal-logout-mobile" className="p-2 rounded-lg hover:bg-slate-100"><LogOut className="h-5 w-5 text-rose-500" /></button></div>
          </div>
        </header>
        <main className="flex-1 p-5">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 grid grid-cols-5 h-16">
        {nav.slice(0, 5).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 text-[10px] ${isActive ? "text-[#0088FF]" : "text-slate-400"}`}>
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
